import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'

import {
  NavigationGroup,
  NavigationItemInterface,
} from '@/layouts/partials/NavigationGroup'
import { useOnClickOutside } from '@/lib/hooks/useOnClickOutside'
import { useAuth } from '@/lib/provider/AuthProvider'
import { useFeedback } from '@/lib/provider/FeedbackProvider'
import { useTenant } from '@/lib/provider/TenantProvider'
import { LEGAL_LINKS, NAVIGATION_LINKS } from '@/lib/definitions/constants'

import MenuIcon from '@icons/bars-3'
import CogIcon from '@icons/cog'
import CloseIcon from '@icons/x-mark'
import ArrowDownIcon from '@icons/chevron-down'
import PhoneIcon from '@icons/phone'
import AtSymbolIcon from '@icons/at-symbol'
import DocumentTextIcon from '@icons/document-text'
import TicketIcon from '@icons/ticket'
import FeedbackIcon from '@icons/chat-bubble-left-right'
import AccountMenu from '@/layouts/partials/AccountMenu'
import DropdownMenu from '@/layouts/partials/DropdownMenu'
import ImpersonationButton from '@/layouts/partials/ImpersonationButton'
import NavBadge from '@/layouts/partials/NavBadge'
import OrganizationButton from '@/layouts/partials/OrganizationButton'
import NavDrawer from '@/layouts/partials/NavDrawer'
import StatusIndicator from '@/layouts/partials/StatusIndicator'

import { FLAGS, useFeatureFlag } from '@/lib/hooks/useFeatureFlag'
import { useInsightsOpensExternally } from '@/lib/hooks/useInsightsNavTarget'
import { useIncomingJoinRequestCount } from '@/lib/hooks/useJoinRequestStatus'
import UserAvatar from '@/components/UserAvatar'
import { cn } from '@/lib/misc/Utils'

const Navigation = () => {
  const [auth] = useAuth()
  const { isFeedbackEnabled, toggleFeedback } = useFeedback()
  const { customerId } = useTenant()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useOnClickOutside(userMenuRef, () => setIsUserMenuOpen(false))
  const isBenocsEnabled = useFeatureFlag(FLAGS.BENOCS)
  const isMultiTenancyEnabled = useFeatureFlag(FLAGS.MULTI_TENANCY)
  const isTicketsEnabled = useFeatureFlag(FLAGS.TICKETS)
  const insightsOpensExternally = useInsightsOpensExternally()
  const incomingJoinRequestCount = useIncomingJoinRequestCount()
  const joinRequestsLabel =
    incomingJoinRequestCount === 1
      ? '1 person wants to join your company'
      : `${incomingJoinRequestCount} people want to join your company`

  const userProfileName: string = useMemo(
    () => auth.user.first_name || auth.user.last_name || auth.user.email,
    [auth.user]
  )

  const userInitials: string = useMemo(() => {
    const { first_name: first, last_name: last, email } = auth.user
    if (first && last) return `${first.charAt(0)}${last.charAt(0)}`
    return String(email ?? first ?? last ?? '')
      .charAt(0)
      .toUpperCase()
  }, [auth.user])

  const settingsHref: string = useMemo(
    () =>
      auth.user.customer_id ? '/settings/users' : '/settings/create-customer',
    [auth.user.customer_id]
  )

  const desktopNavPillItemBaseClass =
    'group flex h-8 shrink-0 cursor-pointer flex-row items-center rounded-lg px-2.5 text-[14px] font-medium leading-5 text-neutral-60 transition-colors hover:bg-neutral-20/70 hover:text-neutral-100'

  const desktopNavPillItemCompactClass = cn(
    desktopNavPillItemBaseClass,
    'space-x-1'
  )

  const desktopNavPillItemActiveClass =
    'bg-accent-10 text-accent-60 hover:bg-accent-20 hover:text-accent-60'

  // customer asn
  const customerAsn = useMemo(() => auth.user.company?.asn, [auth.user])

  const mainMenuItems: NavigationItemInterface[] = useMemo(
    () => [
      { id: 'dashboard', to: '/', title: 'Dashboard' },
      {
        id: 'customers',
        to: '/customers',
        title: 'Customers',
        // Mirrors useMainNavItems: hidden while viewing a customer's portal,
        // because that customer has no customers of its own.
        isHidden:
          Boolean(customerId) ||
          !isMultiTenancyEnabled ||
          !auth.user.is_reseller,
        isActive: window.location.pathname.startsWith('/settings'),
      },
      ...(isBenocsEnabled
        ? [
            {
              id: 'insights',
              to: '/insights',
              title: 'Insights',
              external: insightsOpensExternally,
              showArrow: false,
            },
          ]
        : []),
      {
        id: 'billing',
        to: '/billing',
        title: 'Billing',
      },
      {
        id: 'tickets',
        to: '/tickets',
        title: 'Tickets',
        isHidden: !isTicketsEnabled,
      },
      {
        id: 'tools',
        title: 'Tools',
        links: [
          {
            id: 'api',
            label: 'API',
            to: `${import.meta.env.VITE_API_DOCS_URL}`,
            external: true,
          },
          {
            id: 'knowledge-base',
            label: 'Knowledge Base',
            to: NAVIGATION_LINKS.KNOWLEDGE_BASE,
            external: true,
          },
          {
            id: 'looking-glass',
            label: 'Looking Glass',
            to: NAVIGATION_LINKS.LOOKING_GLASS,
            external: true,
          },
          {
            id: 'bgp-communities',
            label: 'BGP Communities',
            to: NAVIGATION_LINKS.BGP_COMMUNITIES,
            external: true,
          },
          {
            id: 'terraform-provider',
            label: 'Terraform Provider',
            to: NAVIGATION_LINKS.TERRAFORM_PROVIDER,
            external: true,
          },
          {
            id: 'map',
            label: 'Network Map',
            to: LEGAL_LINKS.NETWORK_MAP,
            external: true,
            isSeparated: true,
          },
          {
            id: 'peeringdb-as5405',
            label: 'PeeringDB AS5405',
            to: NAVIGATION_LINKS.PEERINGDB_AS5405,
            external: true,
            isSeparated: true,
          },
          {
            id: 'bgp-tools-as5405',
            label: 'BGP.tools AS5405',
            to: NAVIGATION_LINKS.BGP_TOOLS_AS5405,
            external: true,
          },

          ...(!!customerAsn
            ? [
                {
                  id: 'peeringdb-customer-as',
                  label: `PeeringDB AS${customerAsn}`,
                  to: `https://www.peeringdb.com/asn/${customerAsn}`,
                  external: true,
                },
                {
                  id: 'bgp-tools-customer-as',
                  label: `BGP.tools AS${customerAsn}`,
                  to: `https://bgp.tools/as/${customerAsn}`,
                  external: true,
                },
              ]
            : []),

          {
            id: 'changelog',
            label: 'Changelog',
            to: NAVIGATION_LINKS.CHANGELOG,
            external: true,
            isSeparated: true,
          },
        ],
        alignment: 'left',
      },
    ],
    [
      customerId,
      isMultiTenancyEnabled,
      isBenocsEnabled,
      insightsOpensExternally,
      isTicketsEnabled,
      auth.user,
      customerAsn,
    ]
  )

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  const toggleUserMenu = useCallback(() => {
    setIsUserMenuOpen((open) => !open)
  }, [])

  return (
    <>
      <nav className="sticky top-0 z-30 w-full md:bg-gradient-to-b md:from-canvas md:via-canvas/85 md:to-transparent md:pt-3 md:pb-3">
        <div className="relative flex w-full min-w-0 items-center justify-between max-md:h-14">
          <div className="md:hidden">
            <div className="bg-brand-1 h-14 absolute left-0 top-0 w-full">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
              >
                <img src="/images/logo-wide.svg" alt="Inter.link Logo" />
              </Link>
            </div>

            {isMobileMenuOpen ? (
              <CloseIcon
                className="absolute top-1.5 size-11 p-2.5 z-60 cursor-pointer text-2xl text-white left-[max(0.375rem,env(safe-area-inset-left,0px))]"
                onClick={toggleMobileMenu}
              />
            ) : (
              <MenuIcon
                className="absolute top-1.5 size-11 p-2.5 z-60 cursor-pointer text-2xl text-white left-[max(0.375rem,env(safe-area-inset-left,0px))]"
                onClick={toggleMobileMenu}
              />
            )}

            <div className="absolute top-0 z-60 flex h-14 items-center right-[max(1.25rem,env(safe-area-inset-right,0px))]">
              <StatusIndicator variant="dot" withSupport />
            </div>

            <NavDrawer
              isOpen={isMobileMenuOpen}
              onClose={() => setIsMobileMenuOpen(false)}
              topOffsetClass="top-14"
              label="Main navigation"
              className="w-full max-w-sm border-t border-white/20 bg-brand-1"
            >
              <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto">
                  <div className="px-4 py-6">
                    {mainMenuItems
                      .filter((item) => !item.isHidden)
                      .map((item, index) => {
                        const mobileMainItemClass =
                          index > 0
                            ? 'mt-3 border-t border-white/15 pt-3'
                            : undefined

                        return (
                          <div
                            key={item.id || index}
                            className={mobileMainItemClass}
                          >
                            {item.links ? (
                              <DropdownMenu
                                width={280}
                                id={item.id}
                                links={item.links}
                                alignment="left"
                              >
                                <div className="w-full flex items-center justify-between px-4 py-3 text-left text-white hover:bg-surface/10 rounded-lg transition-colors cursor-pointer">
                                  <span className="font-medium">
                                    {item.title}
                                  </span>
                                  <ArrowDownIcon className="w-5 h-5 text-white/70" />
                                </div>
                              </DropdownMenu>
                            ) : (
                              <NavLink
                                to={item.to || '#'}
                                target={item.external ? '_blank' : '_self'}
                                onClick={toggleMobileMenu}
                                className={({ isActive }) =>
                                  `block px-4 py-3 text-white hover:bg-surface/10 rounded-lg transition-colors font-medium ${
                                    isActive || item.isActive
                                      ? 'bg-surface/20 text-white'
                                      : ''
                                  }`
                                }
                              >
                                {item.title}
                              </NavLink>
                            )}
                          </div>
                        )
                      })}
                  </div>

                  <div className="border-t border-white/20 mx-4" />

                  <div className="px-4 py-6">
                    <AccountMenu tone="dark" onNavigate={toggleMobileMenu} />
                  </div>

                  <div className="px-4 pb-6">
                    <div className="bg-surface/10 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-white mb-3">
                        Support
                      </h3>
                      <div className="space-y-3">
                        <a
                          href="tel:+4930577037417"
                          className="flex min-h-11 items-center space-x-3 text-white/90 hover:text-white transition-colors"
                        >
                          <PhoneIcon className="w-5 h-5" />
                          <span>+49 30 5770 374 17</span>
                        </a>
                        <a
                          href="mailto:support@inter.link"
                          className="flex min-h-11 items-center space-x-3 text-white/90 hover:text-white transition-colors"
                        >
                          <AtSymbolIcon className="w-5 h-5" />
                          <span>support@inter.link</span>
                        </a>
                        <a
                          href={LEGAL_LINKS.ESCALATION_MATRIX}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex min-h-11 items-center space-x-3 text-white/90 hover:text-white transition-colors"
                        >
                          <DocumentTextIcon className="w-5 h-5" />
                          <span>Escalation Matrix</span>
                        </a>
                        {isFeedbackEnabled && (
                          <button
                            type="button"
                            onClick={() => {
                              setIsMobileMenuOpen(false)
                              toggleFeedback()
                            }}
                            className="flex min-h-11 items-center space-x-3 text-white/90 hover:text-white transition-colors"
                          >
                            <FeedbackIcon className="w-5 h-5" />
                            <span>Give feedback</span>
                          </button>
                        )}
                        {isTicketsEnabled && (
                          <Link
                            to="/tickets/new"
                            onClick={toggleMobileMenu}
                            className="flex min-h-11 items-center space-x-3 text-white/90 hover:text-white transition-colors"
                          >
                            <TicketIcon className="w-5 h-5" />
                            <span>Open a support ticket</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </NavDrawer>
          </div>

          <div className="page-shell hidden md:block">
            <div className="flex h-14 min-w-0 items-center gap-2 rounded-2xl border border-hairline bg-surface/80 px-3 shadow-raised backdrop-blur-xl">
              {/* Brand and primary destinations, anchored to the left edge */}
              <Link
                to="/"
                className="flex h-8 shrink-0 items-center pr-1"
                aria-label="Inter.link home"
              >
                <img
                  src="/images/Wide-dark-Paths.svg"
                  alt="Inter.link"
                  className="h-10 w-auto"
                />
              </Link>

              <div className="ml-2 flex min-w-0 flex-1 items-center overflow-x-auto scrollbar-hidden">
                <NavigationGroup
                  group={{
                    className: 'mr-0',
                    items: mainMenuItems,
                  }}
                />
              </div>

              {/* Account cluster */}
              <div
                className="relative flex shrink-0 items-center gap-1"
                ref={userMenuRef}
              >
                <StatusIndicator variant="toolbar" withSupport />
                <div className="flex min-w-0 items-center gap-1">
                  <OrganizationButton
                    className={desktopNavPillItemCompactClass}
                    switcherButtonClassName={cn(
                      desktopNavPillItemCompactClass,
                      'w-8 justify-center px-0'
                    )}
                  />
                  <ImpersonationButton
                    className={cn(
                      desktopNavPillItemCompactClass,
                      'w-8 justify-center px-0'
                    )}
                    activeClassName={desktopNavPillItemActiveClass}
                  />
                  <NavLink
                    data-cy="settings-menu-button"
                    to={settingsHref}
                    className={({ isActive }) =>
                      cn(
                        desktopNavPillItemBaseClass,
                        isActive && desktopNavPillItemActiveClass,
                        'relative w-8 justify-center px-0'
                      )
                    }
                  >
                    <CogIcon className="size-[18px]" />
                    <span className="sr-only">Settings</span>
                    {incomingJoinRequestCount > 0 && (
                      <NavBadge
                        label={joinRequestsLabel}
                        count={incomingJoinRequestCount}
                        overlay
                        tooltip={`${joinRequestsLabel} — review in Users`}
                        cy="settings-join-requests-badge"
                        className="absolute -top-1 -right-1"
                      />
                    )}
                  </NavLink>
                  <button
                    type="button"
                    data-cy="user-menu-button"
                    onClick={toggleUserMenu}
                    className={cn(
                      desktopNavPillItemBaseClass,
                      'gap-2',
                      isUserMenuOpen && desktopNavPillItemActiveClass
                    )}
                  >
                    <UserAvatar initials={userInitials} size="sm" />
                    <span className="hidden max-w-[9rem] min-w-0 truncate text-left transition-colors group-hover:text-neutral-100 sm:inline">
                      {userProfileName}
                    </span>
                    <ArrowDownIcon className="size-3.5 shrink-0 text-neutral-40 transition-colors group-hover:text-neutral-70" />
                  </button>
                </div>

                {isUserMenuOpen && (
                  <AccountMenu
                    onNavigate={() => setIsUserMenuOpen(false)}
                    className="absolute right-0 top-full z-60 mt-2 w-[280px]"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  )
}

export default memo(Navigation)
